/**
 * Per-stroke lump defaults for the swept brush (materials-perf-plan §2.3 / B4, §5 worlds).
 *
 * The registry `stroke` policy of the placed type carries `lumpVoxels` / `lumpCell` (sand 2/4,
 * clay 1/3). `resolveBrushLump` turns them into snapshot fields:
 * - capped at `LUMP_CAP_LARGE_RADIUS` (2 voxels) when the radius exceeds `LUMP_CAP_RADIUS` (16)
 *   until tablet numbers exist (plan §4 row B4), and never larger than the radius itself;
 * - absent when the type sets no lump, or when `huebo:brush-lump=0` (lump treated as 0 for new
 *   strokes; events that already carry a lump still replay lumpy, because replay must be exact).
 *
 * Seeds: the controller picks one per stroke (`strokeSeed`, from the first hit cell and the clock)
 * and stores it in the event; the hover preview uses `hoverSeed(cellKey)`, a pure function of the
 * hovered cell, so a cursor moving inside one cell keeps the same preview event (A1 stability).
 */

import { FX_ONE, toFx } from './fixed'
import { clampLumpFx, hash32 } from './noise'
import { strokePolicy } from '@/voxels/registry'

export const BRUSH_LUMP_STORAGE_KEY = 'huebo:brush-lump'

/** Above this radius (voxels) the lump is capped, until tablet cost is measured. */
export const LUMP_CAP_RADIUS = 16
/** The lump cap (voxels) applied above `LUMP_CAP_RADIUS`. */
export const LUMP_CAP_LARGE_RADIUS = 2

function readFlagOnce(): boolean {
  try {
    if (typeof localStorage === 'undefined') return true
    return localStorage.getItem(BRUSH_LUMP_STORAGE_KEY) !== '0'
  } catch {
    return true
  }
}

let brushLumpOn = readFlagOnce()

/** Is the lumpy shell (B4) applied to new strokes? (`huebo:brush-lump=0` turns it off.) */
export function brushLumpEnabled(): boolean {
  return brushLumpOn
}

/** Overrides the session flag without touching localStorage (tests). */
export function setBrushLumpEnabled(enabled: boolean): void {
  brushLumpOn = enabled
}

export type BrushLump = { lumpFx: number; lumpCell: number }

/**
 * The lump a draw stroke of `type` at `radiusVoxels` uses, or null for a plain sweep. Pure apart
 * from the session flag; the controller and the hover preview both go through it so they agree.
 */
export function resolveBrushLump(type: string | undefined, radiusVoxels: number): BrushLump | null {
  if (!brushLumpOn || !type) return null
  const stroke = strokePolicy(type)
  const lumpVoxels = stroke?.lumpVoxels ?? 0
  if (!(lumpVoxels > 0) || !(radiusVoxels > 0)) return null
  const capped = radiusVoxels > LUMP_CAP_RADIUS ? Math.min(lumpVoxels, LUMP_CAP_LARGE_RADIUS) : lumpVoxels
  const lumpFx = clampLumpFx(toFx(capped), toFx(radiusVoxels))
  if (lumpFx <= 0) return null
  const lumpCell = Math.max(1, Math.round(stroke?.lumpCell ?? 1))
  return { lumpFx, lumpCell }
}

/** Snapshot lump fields for `resolveBrushLump`'s result (spread into a `BrushSnapshot`). */
export function lumpSnapshotFields(lump: BrushLump | null, seed: number): { lumpFx: number; lumpCell: number; seed: number } | {} {
  return lump ? { lumpFx: lump.lumpFx, lumpCell: lump.lumpCell, seed } : {}
}

/** A per-stroke seed from the first hit cell and the stroke start time (plan §2.3). Non-negative int32. */
export function strokeSeed(startKey: number, startMs: number): number {
  return (hash32((startKey >>> 0) ^ hash32(startMs >>> 0)) & 0x7fffffff) >>> 0
}

/** The hover preview's seed: a pure function of the hovered cell, so it is stable within that cell. */
export function hoverSeed(cellKey: number): number {
  return (hash32(cellKey >>> 0) & 0x7fffffff) >>> 0
}

/** Lump in voxels for display / tests. */
export function lumpVoxelsOf(lump: BrushLump | null): number {
  return lump ? lump.lumpFx / FX_ONE : 0
}
